import { useMemo, useState } from "react";
import { Box } from "@mui/material";
import Swal from "sweetalert2";
import { MdDragIndicator } from "react-icons/md";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  BudgetItemTypeEnum,
  CalcOption,
} from "../../../../types/budget/budget";
import type { RenderableRow } from "../../../../utils/budget-groups";
import {
  useBudgetActions,
  useBudgetMeta,
  useBudgetProvider,
  useBudgetServicesState,
  useBudgetSuppliersState,
} from "../../../../contexts/BudgetContext/projectbudgets.context";
import {
  servicesOfRow,
  sumServiceTotals,
} from "../../../../utils/budget-group-totals";
import { toCategoryTotals } from "../../../../utils/budget-group-totals";
import BudgetItem from "../BudgetItem";
import MoveCategoryBillingModal from "../MoveCategoryBillingModal";
import TransferCategorySupplierModal from "../TransferCategorySupplierModal";
import { useCategoryActions } from "../hooks/useCategoryActions";

type PropTypes = {
  row: RenderableRow;
  calcOption: CalcOption;
  /**
   * Número exibido à esquerda: a POSIÇÃO da linha dentro do agrupamento.
   *
   * No protótipo os números eram 1, 3, 5, 8 e se repetiam entre os dias, o que
   * sugeria um índice fixo por categoria — mas nenhuma lista real do sistema
   * produz essa sequência (nem o enum, nem a ordem fixa de categorias), então
   * era numeração do mock. A visão "Por Fornecedor" numera pela posição, e
   * fazer o mesmo aqui mantém as duas coerentes.
   */
  position: number;
  /** Id do sortable — a linha é arrastável entre agrupamentos. */
  sortableId: string;
  /** Nome do agrupamento, usado na confirmação de exclusão da linha. */
  groupName: string;
  readOnly: boolean;
};

/**
 * Uma linha dentro do agrupamento.
 *
 * A linha É a categoria da visão "Por Fornecedor" — o mesmo `BudgetItem`, com
 * `groupId`, mostrando só os serviços deste agrupamento. Este componente não
 * desenha cabeçalho nenhum: ele empacota o `BudgetItem` no arrasto e entrega o
 * fornecedor e os totais recortados para o cabeçalho que já existe.
 *
 * Antes havia aqui uma reprodução do cabeçalho da categoria, e expandir a linha
 * mostrava os DOIS empilhados, com duas setas — foi o que o QA descreveu como
 * "ele replica basicamente o redor da categoria". Um cabeçalho só também
 * garante que as ações (excluir, mover de forma de cobrança, trocar fornecedor)
 * sejam exatamente as mesmas nas duas visões, em vez de duas listas para manter
 * em sincronia.
 */
const GroupRow: React.FC<PropTypes> = ({
  row,
  calcOption,
  position,
  sortableId,
  groupName,
  readOnly,
}) => {
  const { versionInfo, activeBudgetId } = useBudgetMeta();
  const { activeBudgetDispatch, runBudgetAutosave } = useBudgetActions();
  const { budgetServices } = useBudgetServicesState();
  const { paymentSolicitation } = useBudgetSuppliersState();
  const [expanded, setExpanded] = useState(false);
  const [baseValueExpanded, setBaseValueExpanded] = useState(false);

  // As três ações da categoria (excluir, mover de forma de cobrança, transferir
  // fornecedor) vêm do mesmo hook que a visão "Por Fornecedor" usa — são
  // operações com regra própria e não podem divergir entre as telas.
  const categoryActions = useCategoryActions(row.supplierId);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: sortableId,
    disabled: readOnly,
    data: { kind: "row" },
  });

  // O mesmo par (fornecedor, categoria) pode ter serviços em vários
  // agrupamentos; cada linha soma só os seus. A árvore de totais não conhece
  // agrupamento, daí o recálculo (ver `utils/budget-group-totals.ts`).
  const totals = useMemo(
    () =>
      toCategoryTotals(
        sumServiceTotals(
          servicesOfRow(
            budgetServices,
            { ...row, calcOption },
            versionInfo?.version ?? 0,
            versionInfo?.status
          ),
          versionInfo
        )
      ),
    [budgetServices, row, calcOption, versionInfo]
  );

  // `useBudgetProvider`, não `providersById[id]` direto: o id gravado pode vir
  // sufixado (`<id>|dup|N`, `<id>-A-2`) e o mapa é indexado pelo id limpo. Lido
  // cru, a busca falhava e a linha exibia o próprio id — "segue mostrando um
  // código aqui", no vídeo do QA. O hook aplica a mesma limpeza que a visão
  // "Por Fornecedor".
  const provider = useBudgetProvider(row.supplierId);
  const supplierName = provider?.name || "Fornecedor a definir";

  /**
   * O ✕ da linha remove só o que está NESTE agrupamento.
   *
   * A visão "Por Fornecedor" continua com `removeBudgetItem`, que apaga a
   * categoria inteira — lá a linha É a categoria. Aqui a linha é o recorte dela
   * num agrupamento, e apagar o par todo levava junto os outros dias.
   */
  const removerLinhaDoAgrupamento = () => {
    const dentroDeAgrupamento = !!row.groupId;
    Swal.fire({
      title: "Tem certeza?",
      text: dentroDeAgrupamento
        ? `Os serviços de ${
            BudgetItemTypeEnum[row.type]
          } em "${groupName}" serão apagados. Os de outros agrupamentos não são afetados.`
        : `Os serviços de ${
            BudgetItemTypeEnum[row.type]
          } fora de agrupamento serão apagados.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "Não, cancelar",
      confirmButtonText: "Sim, prossiga",
    }).then((result) => {
      if (!result.isConfirmed) return;
      activeBudgetDispatch({
        type: "REMOVE_ROW_FROM_GROUP",
        payload: {
          type: row.type,
          supplierId: row.supplierId,
          calcOption,
          groupId: row.groupId,
        },
        versionInfo,
      });
      if (activeBudgetId) {
        window.setTimeout(() => {
          void runBudgetAutosave("manual");
        }, 0);
      }
    });
  };

  const paymentInfo = paymentSolicitation?.find(
    (el) =>
      el.supplierId === row.supplierId &&
      el.type === row.type &&
      el.calcOption === calcOption
  );

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        outline: isOver && !isDragging ? "2px dashed #1361A4" : "none",
        outlineOffset: "2px",
        borderRadius: "4px",
        mt: 0.75,
      }}
    >
      <BudgetItem
        type={row.type}
        supplierId={row.supplierId}
        supplierName={supplierName}
        groupId={row.groupId}
        totalsOverride={totals}
        headerLeadingSlot={
          readOnly ? undefined : (
            <Box
              {...attributes}
              {...listeners}
              aria-label={`Reordenar ${BudgetItemTypeEnum[row.type]}`}
              // O arrasto começa na alça, não no cabeçalho inteiro: o
              // cabeçalho é o próprio botão de expandir do acordeão.
              onClick={(event) => event.stopPropagation()}
              sx={{
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
                display: "flex",
                alignItems: "center",
                mr: 0.5,
              }}
            >
              <MdDragIndicator size={20} color="rgba(255,255,255,0.5)" />
            </Box>
          )
        }
        headerSubtitle={
          <Box
            sx={{
              display: "block",
              fontSize: 12,
              color: "rgba(255,255,255,0.8)",
              maxWidth: 260,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {supplierName}
          </Box>
        }
        paymentInfo={paymentInfo as never}
        panel={`group-${sortableId}`}
        baseValuePanel={`group-base-${sortableId}`}
        expanded={expanded}
        baseValueExpanded={baseValueExpanded}
        index={position - 1}
        onChange={() => (_event, isOpen) => setExpanded(isOpen)}
        onChangeBaseValue={() => (_event, isOpen) =>
          setBaseValueExpanded(isOpen)}
        onRemoveItem={removerLinhaDoAgrupamento}
        onMoveBillingModel={categoryActions.openMoveBillingModal}
        onTransferSupplier={categoryActions.openTransferModal}
      />

      {categoryActions.moveModalType && (
        <MoveCategoryBillingModal
          open
          onClose={() => categoryActions.setMoveModalType(null)}
          categoryLabel={BudgetItemTypeEnum[categoryActions.moveModalType]}
          supplierLabel={categoryActions.supplierLabel}
          currentCalcOption={calcOption}
          onConfirm={(target) =>
            categoryActions.confirmMoveCategory(
              categoryActions.moveModalType as never,
              target
            )
          }
        />
      )}

      {categoryActions.transferModalType && (
        <TransferCategorySupplierModal
          open
          onClose={() => categoryActions.setTransferModalType(null)}
          categoryLabel={BudgetItemTypeEnum[categoryActions.transferModalType]}
          currentSupplierLabel={categoryActions.supplierLabel}
          currentSupplierRealId={categoryActions.parsedSupplierId}
          calcOption={calcOption}
          existingSuppliers={categoryActions.transferTargets}
          onConfirm={(target) =>
            categoryActions.confirmTransferCategory(
              categoryActions.transferModalType as never,
              target
            )
          }
        />
      )}
    </Box>
  );
};

export default GroupRow;
